import { Box, Stack, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Button } from '../../../../Components/MUIs';
import Icon from '../../../../Assets/Icon/CustomIcon';
import ZoomableImage from '../../../../Components/Ui/ZoomableImage';
import { WORK_LOCATIONS, HAZARD_TYPES } from './M_TbmEntry_BASIC_Schema';

const ETC_LABEL = '기타';

const ACCENT = {
  location: { main: '#2F80ED', soft: 'rgba(47,128,237,0.08)' },
  hazard: { main: '#EB5757', soft: 'rgba(235,87,87,0.08)' },
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#FAFBFC',
    '& fieldset': { borderColor: '#E7EAEE' },
    '&:hover fieldset': { borderColor: '#D3D8E0' },
    '&.Mui-focused fieldset': { borderWidth: '1.5px' },
  },
};

/** 섹션 헤더: 아이콘 + 제목 + 필수 표시 */
function SectionHeader({ icon, iconColor, title, required, hint }) {
  return (
    <Stack sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${iconColor}1A`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1A1A1A' }}>
          {title}
        </Typography>
        {required && (
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              backgroundColor: '#EB5757',
              mt: '-10px',
            }}
          />
        )}
      </Stack>
      {hint && (
        <Typography sx={{ fontSize: '12px', color: '#9AA1AC', mt: 0.25, ml: '34px' }}>
          {hint}
        </Typography>
      )}
    </Stack>
  );
}

/** 다중 선택 토글 칩 */
function ToggleChip({ label, selected, color, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.75,
        py: 0.9,
        borderRadius: '999px',
        border: '1.5px solid',
        borderColor: selected ? color.main : '#E4E7EB',
        backgroundColor: selected ? color.main : '#fff',
        color: selected ? '#fff' : '#5B6472',
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: 1.4,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        WebkitTapHighlightColor: 'transparent',
        boxShadow: selected ? `0 4px 10px -4px ${color.main}99` : 'none',
      }}
    >
      {selected && <Icon.Check width={13} height={13} />}
      {label}
    </Box>
  );
}

function M_TbmEntry_BASIC(props) {
  const fileInputRef = useRef(null);
  const { inputs } = props;

  return (
    <>
      <Stack sx={{ display: 'flex', alignItems: 'start', m: 2, mt: 0, mb: 1 }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
          오늘도 안전한 하루를 위해
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
          회의 내용을 입력해주세요.
        </Typography>
      </Stack>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 2.5,
            border: 1,
            borderColor: '#F0F1F3',
            borderRadius: '20px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 16px rgba(20, 30, 50, 0.05)',
          }}
        >
          {/* 제목: 자동 생성, 수정 불가 */}
          <Box sx={{ mb: 3 }}>
            <SectionHeader
              icon={<Icon.Date width={15} height={15} color="#33ABD1" />}
              iconColor="#33ABD1"
              title="제목"
              hint="TBM(날짜) 형식으로 자동 생성돼요"
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: '12px',
                backgroundColor: '#F3F7F8',
                border: '1px solid #E7EAEE',
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1A1A1A' }}>
                {props.tbmTitle}
              </Typography>
              <Box
                sx={{
                  px: 1,
                  py: 0.4,
                  borderRadius: '999px',
                  backgroundColor: 'rgba(51,171,209,0.12)',
                  color: '#1E88C7',
                  fontSize: '11px',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                자동생성
              </Box>
            </Stack>
          </Box>

          {/* 작업 장소 */}
          <SectionHeader
            icon={<Icon.Location width={15} height={15} color={ACCENT.location.main} />}
            iconColor={ACCENT.location.main}
            title="작업 장소"
            required
            hint="중복 선택 가능"
          />
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: inputs.workLocations.includes(ETC_LABEL) ? 1.5 : 0 }}>
            {WORK_LOCATIONS.map((loc) => (
              <ToggleChip
                key={loc}
                label={loc}
                color={ACCENT.location}
                selected={inputs.workLocations.includes(loc)}
                onClick={() => props.ToggleWorkLocation(loc)}
              />
            ))}
          </Stack>
          {inputs.workLocations.includes(ETC_LABEL) && (
            <TextField
              fullWidth
              size="small"
              placeholder="기타 작업 장소를 입력해주세요."
              value={inputs.etcLocationText}
              onChange={(e) =>
                props.setInputs((prev) => ({
                  ...prev,
                  etcLocationText: e.target.value,
                }))
              }
              sx={{ ...inputSx, mb: 1.5 }}
            />
          )}

          {/* 작업 내용: 선택한 장소별로 각각 작성 */}
          {inputs.workLocations.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <SectionHeader
                icon={<Icon.Notes width={15} height={15} color={ACCENT.location.main} />}
                iconColor={ACCENT.location.main}
                title="작업 내용"
                required
                hint="선택한 장소별로 작성해주세요"
              />
              <Stack spacing={1.5}>
                {inputs.workLocations.map((loc) => (
                  <Box
                    key={loc}
                    sx={{
                      borderRadius: '14px',
                      backgroundColor: ACCENT.location.soft,
                      p: 1.5,
                      borderLeft: `3px solid ${ACCENT.location.main}`,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '12.5px', fontWeight: 700, color: ACCENT.location.main, mb: 0.75 }}
                    >
                      {loc === ETC_LABEL
                        ? `기타${inputs.etcLocationText ? `(${inputs.etcLocationText})` : ''}`
                        : loc}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`${loc} 작업 내용을 입력해주세요.`}
                      value={inputs.locationContents[loc] || ''}
                      onChange={(e) =>
                        props.SetLocationContent(loc, e.target.value)
                      }
                      multiline
                      rows={3}
                      sx={{
                        ...inputSx,
                        '& .MuiOutlinedInput-root': {
                          ...inputSx['& .MuiOutlinedInput-root'],
                          backgroundColor: '#fff',
                        },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* 잠재위험요인 */}
          <Box sx={{ mt: 3 }}>
            <SectionHeader
              icon={<Icon.Warning width={15} height={15} color={ACCENT.hazard.main} />}
              iconColor={ACCENT.hazard.main}
              title="잠재위험요인"
              required
              hint="중복 선택 가능"
            />
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: inputs.hazards.includes(ETC_LABEL) ? 1.5 : 0 }}>
              {HAZARD_TYPES.map((hz) => (
                <ToggleChip
                  key={hz}
                  label={hz}
                  color={ACCENT.hazard}
                  selected={inputs.hazards.includes(hz)}
                  onClick={() => props.ToggleHazard(hz)}
                />
              ))}
            </Stack>
            {inputs.hazards.includes(ETC_LABEL) && (
              <TextField
                fullWidth
                size="small"
                placeholder="기타 잠재위험요인을 입력해주세요."
                value={inputs.etcHazardText}
                onChange={(e) =>
                  props.setInputs((prev) => ({
                    ...prev,
                    etcHazardText: e.target.value,
                  }))
                }
                sx={inputSx}
              />
            )}
          </Box>

          {/* 잠재위험요인 대책 */}
          <Box sx={{ mt: 3 }}>
            <SectionHeader
              icon={<Icon.TaskAlt width={15} height={15} color="#2DB400" />}
              iconColor="#2DB400"
              title="잠재위험요인에 대한 대책"
              required
              hint="작업 위치 및 잠재위험별로 작성해주세요"
            />
            <TextField
              fullWidth
              size="small"
              placeholder="예) F/D Yard - 고소작업 추락 위험: 안전대 체결 및 2인 1조 작업 실시"
              value={inputs.countermeasure}
              onChange={(e) =>
                props.setInputs((prev) => ({
                  ...prev,
                  countermeasure: e.target.value,
                }))
              }
              multiline
              rows={4}
              sx={inputSx}
            />
          </Box>

          {/* 사진 */}
          <Box sx={{ mt: 3 }}>
            <SectionHeader
              icon={<Icon.Camera width={15} height={15} color="#8D8787" />}
              iconColor="#8D8787"
              title="사진"
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box>
                <button
                  type="button"
                  style={{
                    cursor: 'pointer',
                    border: '1.5px dashed #DDE1E6',
                    borderRadius: '14px',
                    color: '#8D8787',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    padding: '8px 16px',
                    width: '90px',
                    height: '92px',
                    backgroundColor: '#FAFBFC',
                  }}
                  onClick={() => {
                    const photoCount = props.tbmUpload.previewList.filter(
                      (img) => !img.deleted
                    ).length;
                    if (photoCount >= props.MAX_PHOTOS) return;
                    if (Capacitor.isNativePlatform()) {
                      props.tbmUpload?.NativeFileChange();
                    } else {
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  <Icon.Camera width={40} height={40} />
                  <p>
                    {
                      props.tbmUpload.previewList.filter((img) => !img.deleted)
                        .length
                    }{' '}
                    / {props.MAX_PHOTOS}
                  </p>
                </button>
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={props.tbmUpload.FileChange}
                />
              </Box>

              <Box sx={{ flex: 1, width: '100%', overflow: 'hidden' }}>
                <Swiper slidesPerView="auto" spaceBetween={8}>
                  {props.tbmUpload.previewList
                    .filter((img) => !img.deleted)
                    .map((item, index) => (
                      <SwiperSlide className="workinfo-slide" key={index}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '90px',
                            height: '92px',
                            border: '1px solid #DFD9D9',
                            borderRadius: '14px',
                            overflow: 'hidden',
                          }}
                        >
                          <ZoomableImage
                            style={{
                              width: '100%',
                              objectFit: 'cover',
                            }}
                            src={item.preview}
                            alt="img"
                          />
                          <button
                            type="button"
                            style={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              border: 'none',
                              backgroundColor: 'transparent',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              props.tbmUpload.DeletePreview(item);
                            }}
                          >
                            <Icon.Delete width={20} height={20} />
                          </button>
                        </Box>
                      </SwiperSlide>
                    ))}
                </Swiper>
              </Box>
            </Box>
          </Box>

          <Button
            name="저장"
            variant="contained"
            fullWidth
            onClick={() => props.SaveLogic1()}
            sx={{
              mt: 3.5,
              height: '44px',
              borderRadius: '14px',
              fontSize: '15px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #34C700 0%, #269900 100%)',
              boxShadow: '0 8px 16px -6px rgba(45,180,0,0.45)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2FB800 0%, #228A00 100%)',
              },
              '&.Mui-disabled': {
                background: '#E4E7EB',
                boxShadow: 'none',
                color: '#B0B6BF',
              },
            }}
            disabled={!props.isValid}
          />
        </Box>
      </Box>
    </>
  );
}

export default M_TbmEntry_BASIC;
